/**
 * Markdown formatting actions applied to a textarea selection.
 *
 * Ported line-by-line from `Sources/JNote/Editor/MarkdownFormatter.swift` —
 * that file is the single source of truth for every cursor/placeholder/
 * unwrap behaviour. Do not "fix" quirks here without checking the Swift
 * side first; the goal is exact parity.
 *
 * Both sides operate on UTF-16 code units (NSString ↔ JS string), so all
 * offsets transfer directly.
 */

export type FormatAction =
  | { type: 'bold' }
  | { type: 'italic' }
  | { type: 'strikethrough' }
  | { type: 'heading'; level: number }
  | { type: 'link' }
  | { type: 'inlineCode' }
  | { type: 'codeBlock' }
  | { type: 'quote' }
  | { type: 'bulletList' }
  | { type: 'numberedList' }
  | { type: 'taskList' }
  | { type: 'horizontalRule' }
  | { type: 'table' }
  | { type: 'align'; side: 'left' | 'center' | 'right' }
  /** Insert an arbitrary Markdown / HTML snippet at the caret. */
  | { type: 'snippet'; text: string };

export interface FormatEdit {
  /** Range of the source text the replacement covers. */
  start: number;
  end: number;
  replacement: string;
  /** Selection to restore after applying the replacement. */
  cursorStart: number;
  cursorEnd: number;
}

/** Applies an action to `text[selectedStart, selectedEnd)` and returns the edit. */
export function computeFormatEdit(
  text: string,
  selectedStart: number,
  selectedEnd: number,
  action: FormatAction,
): FormatEdit {
  const selected = text.slice(selectedStart, selectedEnd);

  switch (action.type) {
    case 'bold':
      return wrapInline(selected, '**', '**', 'bold', selectedStart, selectedEnd);
    case 'italic':
      return wrapInline(selected, '*', '*', 'italic', selectedStart, selectedEnd);
    case 'strikethrough':
      return wrapInline(selected, '~~', '~~', 'text', selectedStart, selectedEnd);
    case 'heading':
      return applyHeading(text, selected, Math.max(1, Math.min(6, action.level)), selectedStart, selectedEnd);
    case 'link':
      return applyLink(selected, selectedStart, selectedEnd);
    case 'inlineCode':
      return wrapInline(selected, '`', '`', 'code', selectedStart, selectedEnd);
    case 'codeBlock':
      return applyCodeBlock(selected, selectedStart, selectedEnd);
    case 'quote':
      return prefixLines(text, selected, '> ', 'quote', selectedStart, selectedEnd);
    case 'bulletList':
      return prefixLines(text, selected, '- ', 'list item', selectedStart, selectedEnd);
    case 'numberedList':
      return prefixLines(text, selected, '1. ', 'list item', selectedStart, selectedEnd);
    case 'taskList':
      return prefixLines(text, selected, '- [ ] ', 'task', selectedStart, selectedEnd);
    case 'horizontalRule':
      return {
        start: selectedStart,
        end: selectedEnd,
        replacement: '\n\n---\n\n',
        cursorStart: selectedStart + 6,
        cursorEnd: selectedStart + 6,
      };
    case 'table':
      return applyTable(selectedStart, selectedEnd);
    case 'align':
      return wrapBlock(
        selected,
        `<div align="${action.side}">\n`,
        '\n</div>',
        'text',
        selectedStart,
        selectedEnd,
      );
    case 'snippet':
      return {
        start: selectedStart,
        end: selectedEnd,
        replacement: action.text,
        cursorStart: selectedStart + action.text.length,
        cursorEnd: selectedStart + action.text.length,
      };
  }
}

// MARK: - Helpers

/** Mirrors wrapInline: empty → placeholder, wrapped → unwrap, else wrap. */
function wrapInline(
  selected: string,
  prefix: string,
  suffix: string,
  placeholder: string,
  start: number,
  end: number,
): FormatEdit {
  if (selected.length === 0) {
    const replacement = prefix + placeholder + suffix;
    return {
      start,
      end,
      replacement,
      cursorStart: start + prefix.length,
      cursorEnd: start + prefix.length + placeholder.length,
    };
  }

  if (
    selected.startsWith(prefix) &&
    selected.endsWith(suffix) &&
    selected.length >= prefix.length + suffix.length
  ) {
    const inner = selected.slice(prefix.length, selected.length - suffix.length);
    return {
      start,
      end,
      replacement: inner,
      cursorStart: start,
      cursorEnd: start + inner.length,
    };
  }

  const replacement = prefix + selected + suffix;
  return {
    start,
    end,
    replacement,
    cursorStart: start + prefix.length,
    cursorEnd: start + prefix.length + selected.length,
  };
}

/** Mirrors wrapBlock (alignment wrappers). */
function wrapBlock(
  selected: string,
  open: string,
  close: string,
  placeholder: string,
  start: number,
  end: number,
): FormatEdit {
  const body = selected.length === 0 ? placeholder : selected;
  const replacement = open + body + close;
  return {
    start,
    end,
    replacement,
    cursorStart: start + open.length,
    cursorEnd: start + open.length + body.length,
  };
}

const HEADING_STRIP = /^#{1,6}\s*/;

function applyHeading(
  text: string,
  selected: string,
  level: number,
  selectedStart: number,
  selectedEnd: number,
): FormatEdit {
  const hashes = '#'.repeat(level) + ' ';

  let start = selectedStart;
  let end = selectedEnd;
  let source = selected;
  if (selected.length === 0) {
    const line = lineContentRange(text, selectedStart);
    start = line.start;
    end = line.end;
    source = text.slice(start, end);
  }

  const rewritten = source
    .split('\n')
    .map((line) => {
      const stripped = line.replace(HEADING_STRIP, '');
      const body = stripped.length === 0 ? 'Heading' : stripped;
      return hashes + body;
    })
    .join('\n');

  return {
    start,
    end,
    replacement: rewritten,
    cursorStart: start + hashes.length,
    cursorEnd: start + hashes.length + Math.max(0, rewritten.length - hashes.length),
  };
}

function applyLink(selected: string, start: number, end: number): FormatEdit {
  if (selected.length === 0) {
    return {
      start,
      end,
      replacement: '[text](url)',
      cursorStart: start + 1,
      cursorEnd: start + 5,
    };
  }
  if (selected.startsWith('http://') || selected.startsWith('https://')) {
    return {
      start,
      end,
      replacement: `[](${selected})`,
      cursorStart: start + 1,
      cursorEnd: start + 1,
    };
  }
  return {
    start,
    end,
    replacement: `[${selected}](url)`,
    cursorStart: start + selected.length + 3,
    cursorEnd: start + selected.length + 6,
  };
}

function applyCodeBlock(selected: string, start: number, end: number): FormatEdit {
  const body = selected.length === 0 ? 'code' : selected;
  const replacement = '```\n' + body + '\n```';
  return {
    start,
    end,
    replacement,
    cursorStart: start + 4,
    cursorEnd: start + 4 + body.length,
  };
}

/**
 * Mirrors the Swift multiline-literal template exactly, including the
 * leading newline (inserts a blank line separation before the table).
 */
const TABLE_TEMPLATE = '\n| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n|  |  |  |\n|  |  |  |\n';

function applyTable(start: number, end: number): FormatEdit {
  // Place the caret in the first header cell ("\n| " = 3 chars).
  return {
    start,
    end,
    replacement: TABLE_TEMPLATE,
    cursorStart: start + 3,
    cursorEnd: start + 3 + 'Column 1'.length,
  };
}

function prefixLines(
  text: string,
  selected: string,
  prefix: string,
  placeholder: string,
  selectedStart: number,
  selectedEnd: number,
): FormatEdit {
  let start = selectedStart;
  let end = selectedEnd;
  let source = selected;
  if (selected.length === 0) {
    const line = lineContentRange(text, selectedStart);
    start = line.start;
    end = line.end;
    const raw = text.slice(start, end);
    source = raw.length === 0 ? placeholder : raw;
  }

  const rewritten = source
    .split('\n')
    .map((line) => (line.startsWith(prefix) ? line : prefix + line))
    .join('\n');

  return {
    start,
    end,
    replacement: rewritten,
    cursorStart: start,
    cursorEnd: start + rewritten.length,
  };
}

/** The current line without its trailing newline character. */
function lineContentRange(text: string, position: number): { start: number; end: number } {
  let start = position;
  while (start > 0 && text[start - 1] !== '\n') start--;
  let end = position;
  while (end < text.length && text[end] !== '\n') end++;
  return { start, end };
}
